import { BaseFeature } from '../../core/base-feature.js';

/**
 * ReleaseFeature - Frontend window into co-agreement release
 *
 * Ending the dynamic is mutual: the partner can PROPOSE release and CONFIRM the
 * Subject's request, but it completes only once BOTH parties have signed (the
 * desktop relay tags every request here as the partner party). On completion all
 * enforcement stops, consent is revoked, and the desktop guides the uninstall.
 * The partner's self-release equivalent doesn't exist — only the Subject has an
 * unconditional exit (that lives in the consent feature).
 */
export class ReleaseFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.pendingRequests = new Map();
        this.currentStatus = null;
    }

    static getMetadata() {
        return {
            id: 'release',
            name: 'Release',
            version: 'v1.0',
            capabilities: ['get_status', 'propose', 'confirm'],
            dependencies: []
        };
    }

    async initialize() { console.log('[Release] Initialized'); }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'status_update':
                this.currentStatus = payload;
                this.emit('statusUpdate', payload);
                break;

            case 'get_status_response':
            case 'propose_response':
            case 'confirm_response':
            case 'cancel_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this.pendingRequests.has(originalId)) {
                    const { resolve } = this.pendingRequests.get(originalId);
                    this.pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Release] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this.pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);
            this.pendingRequests.set(messageId, {
                resolve: (result) => { clearTimeout(timeout); resolve(result); },
                reject
            });
        });
    }

    getStatus() { return this._sendRequest('get_status'); }

    /** Propose ending the dynamic. Applies only after the Subject confirms. */
    propose(dataRetained = true) { return this._sendRequest('propose', { dataRetained }); }

    /** Confirm the Subject's pending release request (the partner's signature). */
    confirm(dataRetained) {
        const payload = dataRetained === undefined ? {} : { dataRetained };
        return this._sendRequest('confirm', payload);
    }

    cancel() { return this._sendRequest('cancel'); }

    async cleanup() {
        for (const [, { reject }] of this.pendingRequests) reject(new Error('Feature cleanup'));
        this.pendingRequests.clear();
    }
}
